import { useTranslation } from "next-i18next";
import Head from "next/head";
import { useRouter } from "next/router";

import type { GetServerSideProps } from "next";

import getTranslations from "../lib/getTranslations";
import HrefLang from "@/components/common/HrefLang";
import { ValueCheckPage } from "@/features/value-check/components/ValueCheckPage";
import {
  getValueCheckCanonicalUrl,
  getValueCheckHreflangUrls,
} from "@/features/value-check/value-check.static";

type ValueCheckRouteProps = {
  /** Localized path per locale; also tells `_app` to skip its own tags. */
  hreflangUrls: Record<string, string>;
};

/**
 * WHO-6534. "What is your item worth?": one photo, what comparable pieces sold for on Whoppah, and
 * "Create ad". No account needed until "Create ad".
 *
 * Localized URL per locale (`config/value-check-paths.js`): nl /nl/waardecheck, de /de/wertcheck,
 * fr /fr/estimation, it /it/valutazione, es /es/tasacion, en /value-check. Behind the Statsig gate
 * `value_check`: a visitor without it gets a 404. Open to everyone since 5 Nov 2026 and in the
 * sitemap (`valueCheckSitemapEntries`).
 */
const ValueCheckRoute = ({ hreflangUrls }: ValueCheckRouteProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const canonicalUrl = getValueCheckCanonicalUrl(locale);
  const title = t(
    "value-check:meta-title",
    "What is your item worth? | Whoppah"
  );
  const description = t(
    "value-check:meta-description",
    "Upload one photo and see what comparable pieces sold for on Whoppah. Free, no account needed."
  );

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />
        <meta key="og-url" property="og:url" content={canonicalUrl} />
      </Head>
      <HrefLang urlMap={hreflangUrls} />
      <ValueCheckPage />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  ValueCheckRouteProps
> = async ({ req, locale }) => {
  // Loaded here, like the Open Home pages: the gate reaches server-only code.
  const { valueCheckGateOn } = await import(
    "@/features/value-check/server/gate.server"
  );

  if (!(await valueCheckGateOn(req))) {
    return { notFound: true };
  }

  return {
    props: {
      ...(await getTranslations(locale, ["value-check", "create-ad-v2"])),
      hreflangUrls: getValueCheckHreflangUrls(),
    },
  };
};

export default ValueCheckRoute;
